import { faChevronDown, faChevronRight, faDownload, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { downloadCsv, safeName } from '~/team_tools/components/csv'
import { PlayerTrendDetail } from '~/team_tools/tournaments/PlayerTrendDetail'
import { Server, ServerLife, getServerLife, purgeServer } from './api'
import { mapLabel } from './maps'

/**
 * Servers that are gone, and what they did while they were here.
 *
 * A destroyed server used to vanish from the board and take its history with
 * it -- the one moment anybody wants to know "was that server worth running"
 * is right after it has been switched off. So it lands here instead, folded
 * away under the live fleet, and opens into a life report: how many played,
 * what it took in fees, its busiest hour, the biggest drop-off, how long it
 * stood. Export takes the report as CSV. Delete removes the record for good.
 */
const money = (v: string | number | null | undefined) => Number(v ?? 0).toFixed(2)

const clock = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })

const days = (a: string, b: string | null) => {
    const ms = new Date(b ?? Date.now()).getTime() - new Date(a).getTime()
    const d = ms / 86_400_000
    return d >= 2 ? `${Math.round(d)} days` : `${Math.round(ms / 3_600_000)} hours`
}

export const DeletedServers = ({ servers, onChanged }: { servers: Server[]; onChanged: () => void }) => {
    const [open, setOpen] = useState(false)
    const [report, setReport] = useState<Server | null>(null)

    if (servers.length === 0) return null

    // Most recently destroyed first. One with no date (destroyed before the
    // column was recorded) goes to the bottom rather than the top.
    const ordered = [...servers].sort(
        (a, b) => (b.destroyed_at ? Date.parse(b.destroyed_at) : 0) - (a.destroyed_at ? Date.parse(a.destroyed_at) : 0)
    )

    return (
        <div className='flex flex-col gap-2 rounded border border-white/10 p-2'>
            <button
                type='button'
                onClick={() => setOpen((v) => !v)}
                className='flex w-full flex-row items-center gap-2 text-left'
            >
                <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className='w-3 text-white/40' />
                <span className='text-[10px] uppercase tracking-widest text-white/50'>Deleted servers</span>
                <span className='tabular-nums text-white/30'>{servers.length}</span>
                <span className='ml-auto text-[10px] text-white/30'>
                    {open ? 'click one for its life report' : 'what they did while they were here'}
                </span>
            </button>

            {open && (
                <div className='flex flex-col gap-1'>
                    {ordered.map((s) => (
                        <div
                            key={s.id}
                            role='button'
                            tabIndex={0}
                            onClick={() => setReport(s)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    setReport(s)
                                }
                            }}
                            className='grid cursor-pointer grid-cols-[14rem,minmax(0,1fr),10rem] items-center gap-3 rounded border border-white/5 px-3 py-1.5 opacity-70 hover:border-white/25 hover:opacity-100'
                        >
                            <div className='flex min-w-0 flex-col'>
                                <span className='truncate font-bold line-through decoration-white/40'>
                                    {s.name ?? s.slot}
                                </span>
                                <span className='truncate text-white/40'>
                                    {s.slot ?? `#${s.id}`} · {s.region} · {mapLabel(s.map_name)}
                                </span>
                            </div>
                            <span className='truncate text-white/40'>
                                stood {days(s.created_at, s.destroyed_at)}
                                {s.destroyed_at ? ` · gone ${clock(s.destroyed_at)}` : ''}
                            </span>
                            <span className='text-right text-[10px] uppercase tracking-widest text-white/30'>
                                life report →
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {report && <LifeReport server={report} onClose={() => setReport(null)} onPurged={onChanged} />}
        </div>
    )
}

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <div className='flex flex-col gap-0.5 rounded border border-white/10 px-3 py-2'>
        <span className='text-[10px] uppercase tracking-widest text-white/40'>{label}</span>
        <span className='tabular-nums text-white'>{value}</span>
        {hint && <span className='text-[10px] text-white/30'>{hint}</span>}
    </div>
)

const LifeReport = ({ server, onClose, onPurged }: { server: Server; onClose: () => void; onPurged: () => void }) => {
    const { addNotification } = useNotification()
    const [life, setLife] = useState<ServerLife | null>(null)
    const [confirming, setConfirming] = useState(false)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        let alive = true
        getServerLife(server.id)
            .then((d) => alive && setLife(d))
            .catch(() => alive && setLife(null))
        return () => {
            alive = false
        }
    }, [server.id])

    /** The report as rows, so it opens in a spreadsheet as-is. One file per
     *  server: the totals up top, the curve underneath. */
    const exportCsv = () => {
        if (!life) return
        const lines: string[][] = [
            ['server', server.name ?? server.slot ?? String(server.id)],
            ['slot', server.slot ?? ''],
            ['region', server.region],
            ['map', mapLabel(server.map_name)],
            ['created', server.created_at],
            ['destroyed', server.destroyed_at ?? ''],
            ['players_total', String(life.players_total)],
            ['sessions_total', String(life.sessions_total)],
            ['kills_total', String(life.kills_total)],
            ['fees_usdc', life.fees.usdc],
            ['fees_airdrop', life.fees.airdrop],
            ['loot_paid_usdc', life.loot_paid.usdc],
            ['loot_paid_point', life.loot_paid.point],
            ['peak_players', String(life.peak.players)],
            ['peak_at', life.peak.at],
            ['busiest_hour', life.busiest_hour],
            ['biggest_drop', String(life.biggest_drop.by)],
            ['biggest_drop_at', life.biggest_drop.at],
            ['avg_session_minutes', String(life.avg_session_minutes)],
            [],
            ['bucket_start', 'players'],
            ...life.buckets.map((b) => [b.at, String(b.players)]),
        ]
        downloadCsv(`${safeName(String(server.slot ?? server.name ?? server.id))}-life`, lines)
    }

    const purge = async () => {
        setBusy(true)
        try {
            await purgeServer(server.id)
            addNotification(`${server.name ?? server.slot} removed for good`, 'success')
            onPurged()
            onClose()
        } catch (err: any) {
            addNotification(err.message, 'error')
        } finally {
            setBusy(false)
        }
    }

    const net = life ? Number(life.fees.usdc) - Number(life.loot_paid.usdc) : 0

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4' onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className='flex max-h-[88vh] w-full max-w-3xl flex-col gap-4 overflow-auto rounded border border-white/20 bg-zinc-900 p-4 text-xs font-mono text-white'
            >
                <div className='flex flex-row items-baseline justify-between gap-3'>
                    <span className='flex min-w-0 flex-row items-baseline gap-2'>
                        <span className='truncate text-sm font-bold'>{server.name ?? server.slot}</span>
                        <span className='text-white/40'>
                            {server.slot} · {server.region} · {mapLabel(server.map_name)} · stood{' '}
                            {days(server.created_at, server.destroyed_at)}
                        </span>
                    </span>
                    <span className='flex shrink-0 flex-row items-center gap-2'>
                        <ActionButton
                            onClick={async () => exportCsv()}
                            disabled={!life}
                            title='Download this report as CSV'
                        >
                            <FontAwesomeIcon icon={faDownload} className='mr-1' /> CSV
                        </ActionButton>
                        <ActionButton
                            onClick={async () => setConfirming(true)}
                            disabled={busy}
                            className='text-red-500'
                            title='Remove this server and its history for good'
                        >
                            <FontAwesomeIcon icon={faTrash} className='mr-1' /> Delete
                        </ActionButton>
                        <button type='button' onClick={onClose} className='text-white/40 underline hover:text-white'>
                            close
                        </button>
                    </span>
                </div>

                {confirming && (
                    <ConfirmBar
                        message={`Remove ${server.name ?? server.slot} for good?`}
                        detail='The server record and this report go. Sessions, fees and kills stay on the players they belong to; only the server they point at is gone. Export first if you want the numbers.'
                        confirmLabel='Delete for good'
                        busy={busy}
                        onCancel={() => setConfirming(false)}
                        onConfirm={purge}
                    />
                )}

                {!life ? (
                    <div className='text-white/30'>Reading its history…</div>
                ) : (
                    <>
                        <div className='text-[10px] uppercase tracking-widest text-white/40'>What it made</div>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
                            <Stat
                                label='Fees taken'
                                value={money(life.fees.usdc)}
                                hint={`+ ${money(life.fees.airdrop)} airdrop`}
                            />
                            <Stat
                                label='Loot paid'
                                value={money(life.loot_paid.usdc)}
                                hint={`+ ${Number(life.loot_paid.point).toFixed(0)} points`}
                            />
                            <Stat
                                label={net >= 0 ? 'BR1 kept' : 'BR1 lost'}
                                value={money(Math.abs(net))}
                                hint='USDC fees less USDC loot'
                            />
                            <Stat
                                label='Per player'
                                value={money(life.players_total ? Number(life.fees.usdc) / life.players_total : 0)}
                                hint='fees over its life'
                            />
                        </div>

                        <div className='text-[10px] uppercase tracking-widest text-white/40'>Who played</div>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
                            <Stat label='Players' value={String(life.players_total)} hint='distinct accounts, ever' />
                            <Stat
                                label='Sessions'
                                value={String(life.sessions_total)}
                                hint={`avg ${life.avg_session_minutes} min`}
                            />
                            <Stat label='Kills' value={String(life.kills_total)} />
                            <Stat label='Peak' value={String(life.peak.players)} hint={clock(life.peak.at)} />
                        </div>

                        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                            <Stat
                                label='Busiest hour'
                                value={life.busiest_hour}
                                hint='most sessions started, across its whole life'
                            />
                            <Stat
                                label='Biggest drop-off'
                                value={`−${life.biggest_drop.by} players`}
                                hint={life.biggest_drop.at ? clock(life.biggest_drop.at) : ''}
                            />
                        </div>

                        {life.buckets.length > 1 && (
                            <PlayerTrendDetail
                                points={life.buckets.map((b) => b.players)}
                                startsAt={life.buckets[0].at}
                                endsAt={life.buckets[life.buckets.length - 1].at}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
